import { ArrowDown, ArrowUp } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { path } from "../data/base";
import {
  buildDominoOrderPayload,
  buildDominoThresholdsPayload,
  type DominoOrderEntry,
} from "../data/cockpit-data";

/**
 * The order the engine evaluates dominoes in, and the thresholds stored beside
 * them.
 *
 * Two facts are stated on screen rather than left for someone to discover:
 * changes apply on the NEXT cycle (nothing moves the moment you save), and the
 * threshold values are stored but not yet read by the engine. A control that
 * silently does nothing is worse than no control, so it says so.
 */
export function DominoReorder({
  order,
  registry,
  thresholds,
}: {
  order: string[];
  registry: DominoOrderEntry[];
  thresholds: Record<string, unknown>;
}) {
  const initialItems = registry.length > 0 ? registry : order.map((id) => ({ id, label: null }));
  const [items, setItems] = useState<DominoOrderEntry[]>(initialItems);
  const [draftThresholds, setDraftThresholds] = useState<Record<string, unknown>>(thresholds);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function move(index: number, delta: number) {
    const next = [...items];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setItems(next);
  }

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const orderResponse = await fetch(path("/config"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(buildDominoOrderPayload(items.map((item) => item.id))),
      });
      if (!orderResponse.ok) {
        setMessage(
          orderResponse.status === 403
            ? "You don't have go-live authority."
            : "Configuration write failed.",
        );
        return;
      }
      const thresholdResponse = await fetch(path("/config"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(buildDominoThresholdsPayload(draftThresholds)),
      });
      if (!thresholdResponse.ok) {
        setMessage(
          thresholdResponse.status === 403
            ? "You don't have go-live authority."
            : "Threshold write failed.",
        );
        return;
      }
      setMessage("Configuration saved. Applies next cycle.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Domino priority + parameters
        </p>
        <div className="flex flex-col gap-2">
          {items.map((item, index) => (
            <div
              key={item.id}
              className="grid gap-3 rounded-lg border border-ks-grey-3 p-3 sm:grid-cols-[auto_1fr_minmax(14rem,18rem)_auto] sm:items-center"
            >
              <span className="text-[13px] font-semibold tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              <div>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-[13px] font-semibold text-ks-ink">{item.id}</span>
                  {item.label && <span className="text-sm text-muted-foreground">{displayLabel(item)}</span>}
                </div>
                <p className="text-xs text-muted-foreground">Higher priority is evaluated first.</p>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Threshold (stored, not yet enforced)
                </span>
                <Input
                  className="min-h-9"
                  value={String(draftThresholds[item.id] ?? "")}
                  aria-label={`${item.id} threshold`}
                  placeholder="Value"
                  onChange={(event) =>
                    setDraftThresholds({ ...draftThresholds, [item.id]: event.target.value })
                  }
                />
                <span className="text-xs leading-5 text-muted-foreground">
                  Saved to machine_config for the engine&apos;s next cycle. The engine does not read this
                  value yet.
                </span>
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => move(index, -1)}
                  aria-label={`Move ${item.id} up`}
                >
                  <ArrowUp aria-hidden="true" />
                </Button>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => move(index, 1)}
                  aria-label={`Move ${item.id} down`}
                >
                  <ArrowDown aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save configuration"}
          </Button>
          <span className="text-xs text-muted-foreground">Posts the full order once.</span>
        </div>
        {message && (
          <p className="text-[13px] font-medium text-muted-foreground" role="status">
            {message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function displayLabel(item: DominoOrderEntry): string {
  if (!item.label) return "";
  const prefix = new RegExp(`^${escapeRegExp(item.id)}\\s*`, "i");
  const label = item.label.replace(prefix, "").trim();
  return label ? `- ${label}` : "";
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
